/**
 * The concrete `SyncTransport` for browser/kiosk-web builds
 * (docs/offline/PHASE-4-OFFLINE-ARCHITECTURE.md). Goes through the existing
 * authenticated API client - no separate device credential, no bespoke
 * fetch/retry logic here - so sign-in, token refresh, and sign-out already
 * behave exactly like every other POS request. `../api`'s `call()` already
 * retries a single silent refresh on 401 before giving up, so a 401 that
 * reaches this transport means that refresh already failed.
 */
import type { SyncPullQuery, SyncPullResponse, SyncPushRequest, SyncPushResponse } from '@kortex/shared';
import { SyncTransportError, type SyncTransport } from '@kortex/pos-local';
import { ApiError, api, qs } from './api';

export class HttpSyncTransport implements SyncTransport {
  async push(request: SyncPushRequest): Promise<SyncPushResponse> {
    return this.call(() => api.post<SyncPushResponse>('/api/sync/push', request));
  }

  async pull(query: SyncPullQuery): Promise<SyncPullResponse> {
    return this.call(() =>
      api.get<SyncPullResponse>(`/api/sync/pull${qs({ deviceId: query.deviceId, since: query.since })}`),
    );
  }

  private async call<T>(request: () => Promise<T>): Promise<T> {
    try {
      return await request();
    } catch (error) {
      throw classify(error);
    }
  }
}

function classify(error: unknown): SyncTransportError {
  if (error instanceof ApiError) {
    if (error.status === 401 || error.status === 403) {
      return new SyncTransportError('AUTH', error.message);
    }
    if (
      error.status === 408 ||
      error.status === 429 ||
      (error.status >= 500 && error.status < 600)
    ) {
      return new SyncTransportError('TRANSIENT', error.message);
    }
    return new SyncTransportError('PERMANENT', error.message);
  }
  // A thrown TypeError from `fetch` itself (offline, DNS failure, CORS) never
  // reached the server at all - always worth retrying with backoff.
  return new SyncTransportError('TRANSIENT', error instanceof Error ? error.message : String(error));
}
